<template>
    <div
        class="@container/mh-sidebar flex-1 min-h-0 max-h-full flex flex-col rounded-2xl border-x-0 border-t-0 border-b border-b-white md:bg-slate-300/30 md:shadow-[inset_0px_2px_4px_0px_rgba(0,0,0,0.06)] md:border-solid"
    >
        <!-- Search and Filter Header -->
        <div
            v-if="!props.hideHeader"
            class="fixed inset-s-0 inset-e-0 inset-be-0 z-1 flex flex-col gap-3 p-4 border border-solid border-white shadow-lg bg-white/80 backdrop-blur-sm min-[380px]:max-md:rounded-[26px] min-[380px]:inset-s-4 min-[380px]:inset-e-4 min-[380px]:inset-be-4 md:relative md:inset-s-auto md:inset-e-auto md:inset-be-auto md:bg-slate-50 md:rounded-t-2xl md:rounded-b-none"
        >
            <div class="flex items-center gap-2">
                <div v-if="$slots.filters" class="grow">
                    <slot name="filters"></slot>
                </div>

                <!-- Filter Dropdown -->
                <vx-dropdown v-if="$slots.filterItems" placement="right" teleport data-test="filter-items">
                    <vx-button size="md" color="muted" aria-label="Sort order">
                        <font-awesome-icon widthAuto :icon="faArrowDownArrowUp"></font-awesome-icon>
                    </vx-button>
                    <template v-slot:items>
                        <slot name="filterItems"></slot>
                    </template>
                </vx-dropdown>

                <!-- Action Buttons Slot -->
                <slot name="actionButtons"></slot>
            </div>

            <!-- Alert Slot -->
            <slot name="alerts"></slot>

            <!-- Error Alert -->
            <vx-alert v-if="error" :closeable="false" color="danger">
                <div class="flex items-center justify-between">
                    <div class="grow">{{ props.errorMessage }}</div>
                    <vx-button @click="$emit('retry')" color="danger" size="sm" data-test="retry-button">
                        Retry
                        <font-awesome-icon widthAuto :icon="faRefresh"></font-awesome-icon>
                    </vx-button>
                </div>
            </vx-alert>
        </div>

        <!-- Content Area -->
        <div
            @scroll="emit('scroll', $event)"
            class="scroll-thin p-4 pr-3 flex-auto overflow-auto rounded-3xl"
            data-test="content-area"
        >
            <!-- Mobile Header -->
            <div class="mb-4 md:hidden">
                <slot name="mobileHeader"></slot>
            </div>

            <!-- Content List -->
            <div class="flex flex-col gap-2 pb-24 md:pb-0">
                <slot name="content" :items="props.items"></slot>

                <!-- Loading State -->
                <template v-if="!props.items.length && props.loading">
                    <div v-for="n in 12" :key="n" class="rounded-[10px] bg-slate-300 animate-pulse h-24" />
                </template>

                <!-- Empty State -->
                <div v-else-if="!props.items.length && !props.loading">
                    <vx-box color="info" class="py-12 text-center px-4">
                        {{ props.emptyMessage }}
                    </vx-box>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { faArrowDownArrowUp, faRefresh } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxBox, VxButton, VxDropdown } from '@voxie/frontend-components';

const props = defineProps({
    // Content props
    items: {
        type: Array,
        default: () => [],
    },
    loading: {
        type: Boolean,
        default: false,
    },
    error: {
        type: Boolean,
        default: false,
    },
    hideHeader: {
        type: Boolean,
        default: false,
    },

    // UI text props
    errorMessage: {
        type: String,
        default: 'Something went wrong when loading data.',
    },
    emptyMessage: {
        type: String,
        default: 'No results found',
    },
});

const emit = defineEmits(['retry', 'scroll']);
</script>
